import type { SamsAccess, SamsAdmin, SamsAdminAction, SamsAdminEvent } from "@/api";

/**
 * Which write buttons an admin may press, mirroring the SAMS server rules in
 * replay_lab/sams_admins.py (`AdminDirectory.revoke`, `set_prod_execution`,
 * `_require_prod_holder`, `_require_other_prod_holder`).
 *
 * This greys out what the server would refuse anyway, with the same reason;
 * the server still decides, and its refusal is shown as it came. One rule is
 * the UI's own: an admin cannot revoke itself here (the server allows it while
 * another admin remains), because that locks the caller out at once.
 * Each function returns null when the action is allowed, or the reason.
 *
 * Server tests of the same rules: Replay tests/test_sams_admins.py
 * `test_directory_reads_grants_and_revokes_through_the_owner_engine` and
 * `test_admin_api_grants_and_revokes_without_a_redeploy`.
 */

/** What the rules read of an admin row: its id, prod access and whether it is revoked. */
export type AdminRuleRow = Pick<SamsAdmin, "user_id" | "can_execute_prod" | "revoked_at">;

export function isActive(row: AdminRuleRow) {
  return row.revoked_at === null || row.revoked_at === undefined;
}

function otherProdHolders(rows: AdminRuleRow[], userId: string) {
  return rows.filter((r) => isActive(r) && r.can_execute_prod && r.user_id !== userId).length;
}

/**
 * Why a switch is disabled. `text` is what the admin reads (no codes); `code` is
 * the server refusal it mirrors (or a UI-only reason), for tests and tooling.
 */
export type BlockedReason = { code: string; text: string };

export const SELF_REVOKE_REASON: BlockedReason = {
  code: "SELF_REVOKE", text: "자기 자신은 회수할 수 없습니다. 다른 관리자에게 요청하세요." };

/** `POST /{id}/revoke` (sams_admins.py `AdminDirectory.revoke`, :385-421). */
export function revokeBlockedReason(row: AdminRuleRow, me: SamsAccess, rows: AdminRuleRow[]): BlockedReason | null {
  if (!isActive(row)) return { code: "ADMIN_NOT_ACTIVE", text: "이미 회수된 관리자입니다." };
  // UI-only: revoking yourself ends your own access immediately.
  if (row.user_id === me.user_id) return SELF_REVOKE_REASON;
  if (!rows.some((r) => isActive(r) && r.user_id !== row.user_id)) {
    return { code: "LAST_ADMIN", text: "마지막 활성 관리자는 회수할 수 없습니다." };
  }
  if (row.can_execute_prod) {
    if (!me.prod_access) {
      return { code: "PROD_HOLDER_REVOKE_FORBIDDEN",
               text: "prod 권한자는 prod 권한이 있는 관리자만 회수할 수 있습니다." };
    }
    if (otherProdHolders(rows, row.user_id) === 0) {
      return { code: "LAST_PROD_HOLDER", text: "마지막 prod 권한자는 회수할 수 없습니다." };
    }
  }
  return null;
}

/**
 * `POST /{id}/prod-execution` with `allowed` = the opposite of the row's current value
 * (sams_admins.py `set_prod_execution`, :423-458). Checked in the server's order:
 * SELF_PROD_ACCESS, PROD_GRANT_FORBIDDEN, LAST_PROD_HOLDER (turning off), ADMIN_NOT_ACTIVE.
 */
export function prodToggleBlockedReason(row: AdminRuleRow, me: SamsAccess, rows: AdminRuleRow[]): BlockedReason | null {
  if (row.user_id === me.user_id) {
    return { code: "SELF_PROD_ACCESS", text: "자기 자신의 prod 권한은 바꿀 수 없습니다." };
  }
  if (!me.prod_access) {
    return { code: "PROD_GRANT_FORBIDDEN", text: "prod 권한은 prod 권한이 있는 관리자만 부여·회수합니다." };
  }
  if (row.can_execute_prod && otherProdHolders(rows, row.user_id) === 0) {
    return { code: "LAST_PROD_HOLDER", text: "마지막 prod 권한자는 권한을 잃을 수 없습니다." };
  }
  if (!isActive(row)) return { code: "ADMIN_NOT_ACTIVE", text: "회수된 관리자입니다." };
  return null;
}

export const ACTION_LABELS: Record<SamsAdminAction, string> = {
  seed: "초기 등록",
  grant: "관리자 추가",
  revoke: "관리자 회수",
  grant_prod: "prod 권한 부여",
  revoke_prod: "prod 권한 회수",
};

export function actionLabel(action: string) {
  return (ACTION_LABELS as Record<string, string>)[action] ?? action;
}

/** An admin-directory event (one of the five actions) names its admin by email; others by target. */
export function isAdminEvent(event: Pick<SamsAdminEvent, "action">) {
  return Object.prototype.hasOwnProperty.call(ACTION_LABELS, event.action);
}

/** The server's effective actor kind: `seed` is the owner-plan seed, `system` a job (`system:<name>`). */
export function isSeedEvent(event: Pick<SamsAdminEvent, "actor_kind">) {
  return event.actor_kind === "seed";
}

export function isSystemEvent(event: Pick<SamsAdminEvent, "actor_kind">) {
  return event.actor_kind === "system";
}

export const OUTCOME_LABELS: Record<NonNullable<SamsAdminEvent["outcome"]>, string> = {
  ok: "성공",
  failed: "실패",
  rejected: "거부",
  unknown: "불명",
  pending: "대기",
};

/** An outcome's label; one the UI does not know is shown as it came. */
export function outcomeLabel(outcome: string) {
  return (OUTCOME_LABELS as Record<string, string>)[outcome] ?? outcome;
}

export const MASKED_TEXT = "prod 권한 필요(가려짐)";

/**
 * What the 대상 column reads: an admin event's email; otherwise
 * `target_type target_id`, with the server's one-line summary. A masked
 * event keeps its type and says why the rest is hidden.
 */
export function eventTarget(event: SamsAdminEvent): { text: string; summary: string | null } {
  if (isAdminEvent(event) && event.email) return { text: event.email, summary: null };
  const kind = event.target_type ?? "";
  if (event.masked) return { text: `${kind} ${MASKED_TEXT}`.trim(), summary: null };
  const text = [kind, event.target_id ?? ""].filter(Boolean).join(" ") || event.email || "-";
  return { text, summary: event.summary };
}

/** `detail` as `key: value` lines (scalars from the server; anything else as JSON). */
export function detailEntries(detail: SamsAdminEvent["detail"]): [string, string][] {
  if (!detail) return [];
  return Object.entries(detail).map(([key, value]) => [key,
    value === null || ["string", "number", "boolean"].includes(typeof value) ? String(value) : JSON.stringify(value)]);
}
